// Ui.Tooltip wraps a control it is handed as opaque Html, so in Elm nothing
// can travel between the bubble and the thing it describes. This wires them in
// the DOM: the bubble becomes a named tooltip and the control points at it, so
// no call site has to invent an id.
//
// When there is nothing focusable inside — plain text, or a disabled control —
// the wrapper takes the focus itself, because a bubble that only appears on
// hover is one a keyboard can never reach.

const TEXT = ".tooltip__text";
const END = "tooltip__text--end";
const EDGE = 8;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DESCRIBABLE = 'a[href], button, input:not([type="hidden"]), select, textarea';

let tooltipIds = 0;

const added = new WeakMap();

function describe(node, tooltipId) {
  const ours = added.get(node);
  added.set(node, tooltipId);
  const ids = [
    ...new Set(
      (node.getAttribute("aria-describedby") ?? "")
        .split(" ")
        .filter((id) => id && id !== ours)
        .concat(tooltipId),
    ),
  ];

  node.setAttribute("aria-describedby", ids.join(" "));
}

class ElmTooltip extends HTMLElement {
  connectedCallback() {
    this.tooltipId ??= `elm-tooltip-${++tooltipIds}`;
    this.wire();
    this.addEventListener("pointerenter", this.place);
    this.addEventListener("focusin", this.place);
    this.observer = new MutationObserver(() => this.wire());
    this.observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  place = () => {
    const text = this.own(TEXT);
    if (!text) return;

    text.classList.remove(END);
    const overflows = text.getBoundingClientRect().right > document.documentElement.clientWidth - EDGE;
    text.classList.toggle(END, overflows);
  };

  own(selector) {
    const found = this.querySelector(selector);

    return found && found.closest("elm-tooltip") === this ? found : null;
  }

  wire() {
    const text = this.own(TEXT);
    if (!text) return;

    text.id ||= `${this.tooltipId}-text`;
    text.setAttribute("role", "tooltip");

    const focusable = this.own(FOCUSABLE);
    const described = focusable ?? this.own(DESCRIBABLE);

    if (described) describe(described, text.id);

    if (focusable) {
      this.removeAttribute("tabindex");
      this.removeAttribute("aria-describedby");
    } else {
      this.tabIndex = 0;
      describe(this, text.id);
    }
  }
}

if (!customElements.get("elm-tooltip")) {
  customElements.define("elm-tooltip", ElmTooltip);
}
